// src/services/notifications/LocalNotificationService.js

import { LocalNotifications } from '@capacitor/local-notifications';
import { Capacitor } from '@capacitor/core';
import { MESSAGES, pick } from './messages';

// ── IDs fixos por categoria (evita conflito de IDs) ──────────────────────────
const ID = {
  STREAK_RISK:       1001,
  STREAK_MILESTONE:  1002,
  WORKOUT_BEFORE:    1003,
  WORKOUT_CHECKIN:   1004,
  WORKOUT_NO_ACT:    1005,
  WORKOUT_PLAN:      1006,
  MEAL_LUNCH:        1007,
  MEAL_DINNER:       1008,
  MEAL_MISSED_LUNCH: 1009,
  MEAL_MISSED_DINNER:1010,
  XP_LEVEL_UP:       1011,
  CLUBE_CONVITE:     1012,
};

// IDs avulsos que o cancelAll() do reagendamento NÃO pode apagar.
// 3001 = lembrete do fim do teste grátis (features/monetizacao/lembreteLocal.js).
export const IDS_PRESERVADOS = [3001];

// ── Quantos dias à frente reagendamos as notificações recorrentes ─────────────
// iOS limita a 64 notificações locais agendadas por app. Com ~4 recorrentes × 7
// dias = 28, mais as pontuais, ficamos muito abaixo do teto com folga segura.
const DAYS_AHEAD = 7;

// ── Helper: próxima ocorrência de um horário ──────────────────────────────────
function nextOccurrence(hour, minute = 0) {
  const d = new Date();
  d.setHours(hour, minute, 0, 0);
  if (d <= new Date()) d.setDate(d.getDate() + 1);
  return d;
}

// ── Helper: notificar com segurança ──────────────────────────────────────────
async function scheduleOne({ id, title, body, at, repeats = false, extra = {}, channelId = 'lesvita_main' }) {
  try {
    // Para notificações diárias, garante que o 'at' sempre seja futuro
    const scheduleAt = at > new Date() ? at : (() => {
      const d = new Date(at);
      d.setDate(d.getDate() + 1);
      return d;
    })();

    // // console.log(`[LocalNotif] Agendando id=${id} | "${title}" | ${scheduleAt.toLocaleTimeString()} | repeats=${repeats}`);

    await LocalNotifications.schedule({
      notifications: [{
        id,
        title,
        body,
        schedule: {
          at:             scheduleAt,
          allowWhileIdle: true,
          // ← só passa every se realmente repetir
          ...(repeats ? { every: 'day' } : {}),
        },
        extra:     { ...extra, scheduledAt: scheduleAt.toISOString() },
        smallIcon: 'ic_stat_lesvita',
        largeIcon: 'ic_launcher',
        iconColor: '#0C1B82',
        channelId,
        sound:     null,
      }],
    });

    // // console.log(`[LocalNotif] ✅ Agendado id=${id}`);
  } catch (e) {
    console.warn(`[LocalNotif] ❌ Falha id=${id}:`, e);
  }
}

// ── Helper: agenda uma recorrente como N ocorrências diárias explícitas ────────
// Substitui o padrão `repeats:true / every:'day'` (instável no iOS, onde costuma
// disparar uma vez e parar). Agenda os próximos DAYS_AHEAD dias individualmente,
// cada dia com seu próprio ID (baseId*10 + offset) para não colidir.
// Uma mensagem é sorteada por dia, dando variedade ao usuário.
async function scheduleDaily({ baseId, hour, minute = 0, messages, title, body, extra = {}, channelId = 'lesvita_main' }) {
  if (!Capacitor.isNativePlatform()) return;
  try {
    const now           = new Date();
    const notifications = [];

    for (let i = 0; i < DAYS_AHEAD; i++) {
      const at = new Date();
      at.setHours(hour, minute, 0, 0);
      at.setDate(at.getDate() + i);

      // Pula ocorrências no passado (ex.: o horário de hoje já passou).
      // iOS ignora silenciosamente notificações com `at` no passado.
      if (at <= now) continue;

      const msg = (messages && messages.length) ? pick(messages) : { title, body };

      notifications.push({
        id:    baseId * 10 + i,
        title: msg.title,
        body:  msg.body,
        schedule: { at, allowWhileIdle: true },
        extra:     { ...extra, scheduledAt: at.toISOString(), dayOffset: i },
        smallIcon: 'ic_stat_lesvita',
        largeIcon: 'ic_launcher',
        iconColor: '#0C1B82',
        channelId,
        sound:     null,
      });
    }

    if (notifications.length) {
      await LocalNotifications.schedule({ notifications });
    }
  } catch (e) {
    console.warn(`[LocalNotif] ❌ Falha scheduleDaily base=${baseId}:`, e);
  }
}

// ── LocalNotificationService ──────────────────────────────────────────────────
export const LocalNotificationService = {

  // ── Permissão ───────────────────────────────────────────────────────────────
  async requestPermission() {
    if (!Capacitor.isNativePlatform()) return false;
    try {
      const res = await LocalNotifications.requestPermissions();
      return res.display === 'granted';
    } catch {
      return false;
    }
  },

  async hasPermission() {
    if (!Capacitor.isNativePlatform()) return false;
    try {
      const res = await LocalNotifications.checkPermissions();
      return res.display === 'granted';
    } catch {
      return false;
    }
  },

  // ── Cancelar ────────────────────────────────────────────────────────────────
  async cancelAll() {
    try {
      const pending = await LocalNotifications.getPending();
      // Lembretes AVULSOS (ex.: fim do teste grátis, 3001) não são recorrentes
      // e não seriam reagendados pelo scheduleAll: ficam de fora da limpeza.
      const recorrentes = pending.notifications.filter(
        (n) => !IDS_PRESERVADOS.includes(Number(n.id)),
      );
      if (recorrentes.length > 0) {
        await LocalNotifications.cancel({ notifications: recorrentes });
      }
    } catch (e) {
      console.warn('[LocalNotif] Erro ao cancelar notificações:', e);
    }
  },

  async cancelById(ids) {
    try {
      await LocalNotifications.cancel({ notifications: ids.map(id => ({ id })) });
    } catch {}
  },

  /**
   * Cancela todas as variantes diárias (baseId*10 + 0..6) de uma ou mais
   * categorias recorrentes. Também limpa o ID "legado" (baseId puro) caso
   * tenha sobrado de uma versão anterior que usava every:'day'.
   */
  async cancelByBase(baseIds) {
    try {
      const ids = [];
      baseIds.forEach((b) => {
        ids.push(b); // legado
        for (let i = 0; i < DAYS_AHEAD; i++) ids.push(b * 10 + i);
      });
      await LocalNotifications.cancel({ notifications: ids.map(id => ({ id })) });
    } catch {}
  },

  /** Quantidade de notificações ainda pendentes (agendadas e não disparadas). */
  async getPendingCount() {
    if (!Capacitor.isNativePlatform()) return 0;
    try {
      const pending = await LocalNotifications.getPending();
      return pending?.notifications?.length ?? 0;
    } catch {
      return 0;
    }
  },

  // ── STREAK ──────────────────────────────────────────────────────────────────

  /**
   * Risco de perder ofensiva — agendada para 20h todo dia.
   * Só faz sentido mostrar se streak > 0.
   */
  async scheduleStreakRisk(streakDays) {
    if (streakDays <= 0) return;
    await scheduleDaily({
      channelId: 'lesvita_main',
      baseId:    ID.STREAK_RISK,
      hour:      20,
      minute:    0,
      messages:  MESSAGES.streak.risk,
      extra:     { type: 'streak_risk', streakDays },
    });
  },

  /**
   * Celebração de milestone (7, 14, 30, 60, 100 dias).
   * Dispara em 10s — imediato ao detectar.
   */
  async scheduleStreakMilestone(days) {
    const msg = MESSAGES.streak.milestone(days);
    await scheduleOne({
    channelId: 'lesvita_main',
      id: ID.STREAK_MILESTONE,
      ...msg,
      at: new Date(Date.now() + 10_000),
      extra: { type: 'streak_milestone', days },
    });
  },

  // ── TREINO ──────────────────────────────────────────────────────────────────

  /**
   * Lembrete antes do treino.
   * typicalHour: hora estimada do treino (ex: 7)
   * Notifica ~45min antes.
   */
  async scheduleWorkoutBefore(typicalHour) {
    // Subtrai 45min com proteção de hora mínima (5h)
    const notifHour = Math.max(5, typicalHour - 1);
    const notifMin  = 15; // HH:15
    await scheduleDaily({
      channelId: 'lesvita_main',
      baseId:    ID.WORKOUT_BEFORE,
      hour:      notifHour,
      minute:    notifMin,
      messages:  MESSAGES.workout.before,
      extra:     { type: 'workout_before', typicalHour },
    });
  },

  /**
   * Lembrete de check-in pós-treino.
   * Notifica 2h após a hora típica de treino.
   */
  async scheduleWorkoutCheckin(typicalHour) {
    const notifHour = Math.min(22, typicalHour + 2);
    await scheduleDaily({
      channelId: 'lesvita_main',
      baseId:    ID.WORKOUT_CHECKIN,
      hour:      notifHour,
      minute:    0,
      messages:  MESSAGES.workout.checkin,
      extra:     { type: 'workout_checkin', typicalHour },
    });
  },

  /**
   * "Faz X dias sem treino" — agendada para 10h.
   * daysSince: número de dias sem atividade.
   * Só mostra a partir de 3 dias.
   */
  async scheduleNoActivity(daysSince) {
    if (daysSince < 3) return;
    const msg = MESSAGES.workout.noActivity(daysSince);
    await scheduleOne({
      channelId: 'lesvita_main',
      id: ID.WORKOUT_NO_ACT,
      ...msg,
      at: nextOccurrence(10, 0),
      extra: { type: 'no_activity', daysSince },
    });
  },

  /**
   * "Hoje é dia de treino" — agendada 1h antes do treino.
   */
  async scheduleWorkoutPlanDay(typicalHour) {
    const notifHour = Math.max(6, typicalHour - 1);
    await scheduleDaily({
      channelId: 'lesvita_main',
      baseId:    ID.WORKOUT_PLAN,
      hour:      notifHour,
      minute:    0,
      messages:  MESSAGES.workout.planDay,
      extra:     { type: 'workout_plan' },
    });
  },

  // ── REFEIÇÕES ────────────────────────────────────────────────────────────────

  /**
   * Lembretes de almoço e janta.
   * lunchHour / dinnerHour: hora inteligente detectada ou padrão.
   */
  async scheduleMealReminders({ lunchHour = 12, dinnerHour = 19 } = {}) {
    await scheduleDaily({
      channelId: 'lesvita_main',
      baseId:    ID.MEAL_LUNCH,
      hour:      lunchHour,
      minute:    0,
      messages:  MESSAGES.meal.lunch,
      extra:     { type: 'meal_lunch' },
    });

    await scheduleDaily({
      channelId: 'lesvita_main',
      baseId:    ID.MEAL_DINNER,
      hour:      dinnerHour,
      minute:    0,
      messages:  MESSAGES.meal.dinner,
      extra:     { type: 'meal_dinner' },
    });
  },

  /**
   * Lembrete de refeição perdida — agendado 2h após o horário típico.
   * meal: 'lunch' | 'dinner'
   */
  async scheduleMissedMeal(meal, typicalHour) {
    const msgs = meal === 'lunch' ? MESSAGES.meal.missedLunch : MESSAGES.meal.missedDinner;
    const msg  = pick(msgs);
    const id   = meal === 'lunch' ? ID.MEAL_MISSED_LUNCH : ID.MEAL_MISSED_DINNER;
    await scheduleOne({
      channelId: 'lesvita_main',
      id,
      ...msg,
      at: nextOccurrence(Math.min(22, typicalHour + 2), 0),
      extra: { type: `meal_missed_${meal}` },
    });
  },

  // ── XP / NÍVEL ──────────────────────────────────────────────────────────────

  /**
   * "Faltam X XP para o próximo nível" — agendada para 16h30 estratégico.
   * Só mostra se faltam <= 120 XP.
   */
  async scheduleXpLevelUp(xpNeeded, nextLevel) {
    if (xpNeeded > 120) return;
    const msg = MESSAGES.xp.closeToLevelUp(xpNeeded, nextLevel);
    await scheduleOne({
      channelId: 'lesvita_main',
      id: ID.XP_LEVEL_UP,
      ...msg,
      at: nextOccurrence(16, 30),
      extra: { type: 'xp_level_up', xpNeeded, nextLevel },
    });
  },

  // ── CLUBES ──────────────────────────────────────────────────────────────────

  /**
   * Convite para criar um clube e jogar entre amigos e família: dois por
   * semana, em horário de pausa (19h30 e 12h30), nunca de madrugada. Quem decide SE agenda é o useNotifications
   * (feature ligada, fora de qualquer clube, teto de semanas).
   * O toque abre a criação de clube (extra.tela → abrirTelaGlobal).
   */
  async scheduleClubInvite(datas = []) {
    if (!Capacitor.isNativePlatform()) return;
    // As DATAS vêm fixadas por semana (useNotifications): o reagendamento roda
    // a cada ~20h, e uma data relativa ("daqui a 2 dias") escorregaria para a
    // frente a cada vez e nunca dispararia.
    const agora = new Date();
    const notifications = datas.map((iso, i) => ({ at: new Date(iso), i }))
      .filter(({ at }) => !Number.isNaN(at.getTime()) && at > agora)
      .map(({ at, i }) => {
      const msg = pick(MESSAGES.clubes.convite);
      return {
        id: ID.CLUBE_CONVITE * 10 + i,
        title: msg.title,
        body: msg.body,
        schedule: { at, allowWhileIdle: true },
        extra: { type: 'clube_convite', tela: 'cl:criar', scheduledAt: at.toISOString() },
        smallIcon: 'ic_stat_lesvita',
        largeIcon: 'ic_launcher',
        iconColor: '#0C1B82',
        channelId: 'lesvita_main',
        sound: null,
      };
    });
    if (!notifications.length) return;
    try {
      await LocalNotifications.schedule({ notifications });
    } catch (e) {
      console.warn('[LocalNotif] ❌ Falha convite de clube:', e);
    }
  },

  async testNow(message = "Teste LesVita 🔥") {
    if (!Capacitor.isNativePlatform()) {
      // // console.log('[LocalNotif] TEST (web - simulado):', message);
      // Testa via browser Notification API no web
      if ('Notification' in window) {
        if (Notification.permission === 'granted') {
          new Notification('LesVita', { body: message });
        } else {
          const perm = await Notification.requestPermission();
          if (perm === 'granted') new Notification('LesVita', { body: message });
        }
      }
      return;
    }
    await scheduleOne({
      id:      9999,
      title:   '🔥 Teste LesVita',
      body:    message,
      at:      new Date(Date.now() + 5_000), // 5 segundos
      repeats: false,
      extra:   { type: 'test' },
    });
    // // console.log('[LocalNotif] Notificação de teste agendada para 5s...');
  },

  async debugPending() {
    if (!Capacitor.isNativePlatform()) {
      // // console.log('[LocalNotif] debugPending: não disponível no web');
      return;
    }
    const pending = await LocalNotifications.getPending();
    // console.table(
    //   pending.notifications.map(n => ({
    //     id:    n.id,
    //     title: n.title,
    //     at:    n.schedule?.at,
    //     every: n.schedule?.every ?? '—',
    //   }))
    // );
    // // console.log(`[LocalNotif] Total agendadas: ${pending.notifications.length}`);
  },
};